import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/auth.store';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/cms-portal';

export const developerApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Crucial for SuperTokens session cookies (sAccessToken)
});

// Developer Request Interceptor:
// Developer authentication uses SuperTokens HTTP-only session cookies (sAccessToken) via withCredentials: true.
// We only attach Authorization header if an explicit developer API token is provided.
developerApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken, user } = useAuthStore.getState();
    if (
      accessToken &&
      user?.role === 'partner_dev' &&
      accessToken !== 'cookie-session' &&
      accessToken !== 'fake-jwt-token-12345' &&
      accessToken !== 'dev-token' &&
      accessToken !== 'dev-session-token'
    ) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);


// Developer Response Interceptor: Handle Developer Session Refresh & 401 Auto-logout
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

developerApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Ignore refresh logic for login/refresh/logout/verify-otp endpoints to prevent recursion
    const isAuthEndpoint =
      originalRequest?.url?.includes('/v1/auth/login') ||
      originalRequest?.url?.includes('/v1/auth/refresh') ||
      originalRequest?.url?.includes('/api/auth/session/refresh') ||
      originalRequest?.url?.includes('/v1/auth/logout') ||
      originalRequest?.url?.includes('/v1/auth/verify-otp');

    if (error.response?.status === 401 && !originalRequest?._retry && !isAuthEndpoint) {
      const { user, logout } = useAuthStore.getState();

      if (user?.role !== 'partner_dev') {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => developerApi(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Call Developer Refresh Session endpoint: POST /api/auth/session/refresh
        // SuperTokens sends sRefreshToken cookie and updates session cookies (sAccessToken) on 200 response
        await axios.post(
          `${API_BASE_URL}/api/auth/session/refresh`,
          {},
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );

        processQueue(null);
        return developerApi(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError);
        logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

